import React from 'react'
import clsx from 'clsx'

interface UserPresenceIndicatorProps {
  users: Array<{
    id?: string
    user_id?: string  // WebSocket uses user_id
    username: string
    full_name?: string
    cursor_position?: number
  }>
  currentUserId?: string
  onUserClick?: (userId: string) => void
}

/**
 * Component to display active users' presence indicators.
 * Shows first 2 letters of username in a colored circle.
 * Similar to Google Docs presence indicators.
 */
export const UserPresenceIndicator: React.FC<UserPresenceIndicatorProps> = ({
  users,
  currentUserId,
  onUserClick
}) => {
  // Show all users including current user
  // Handle both 'id' and 'user_id' properties
  const allUsers = users.filter(user => {
    const userId = user.id || user.user_id
    return userId // Only filter out users without IDs
  })
  
  if (!allUsers || allUsers.length === 0) {
    return null
  }

  // Generate consistent color for each user based on their ID
  const getUserColor = (userId: string | undefined): string => {
    if (!userId) {
      return 'bg-gray-500' // Default color if no ID
    }
    
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-yellow-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500'
    ]
    // Use hash of user ID to pick color
    let hash = 0
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  // Get initials (first 2 letters of username or full name)
  const getInitials = (user: { username: string; full_name?: string }): string => {
    const name = user.full_name || user.username
    if (name.length >= 2) {
      return name.substring(0, 2).toUpperCase()
    }
    return name.toUpperCase().padEnd(2, name[0] || 'U')
  }

  return (
    <div className="flex items-center gap-2">
      {allUsers.map((user) => {
        const userId = user.id || user.user_id
        if (!userId) {
          return null // Skip users without ID
        }
        
        const isCurrentUser = userId === currentUserId
        
        return (
          <div
            key={userId}
            className={clsx(
              'relative group',
              onUserClick && 'cursor-pointer'
            )}
            onClick={() => onUserClick?.(userId)}
            title={`${user.full_name || user.username || 'Unknown'}${isCurrentUser ? ' (You)' : ''}${user.cursor_position !== undefined ? ` (position: ${user.cursor_position})` : ''}`}
          >
            <div
              className={clsx(
                'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white',
                getUserColor(userId),
                'hover:ring-2 hover:ring-offset-1 hover:ring-gray-400 transition-all',
                isCurrentUser && 'ring-2 ring-offset-1 ring-blue-400' // Highlight current user
              )}
            >
              {getInitials(user)}
            </div>
            {/* Online indicator dot */}
            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 border-2 border-white rounded-full" />
          </div>
        )
      })}
    </div>
  )
}

